import { configureLogger } from '@hds/administration-client-sdk'
import {
    configureBackends,
    configurePorts,
    createDeviceStorage,
    createWebStorageDriver,
    discoverServices
} from '@hds/auto-auction-com-client-sdk'

/**
 * Where this app finds its services, and where its log entries go.
 *
 * Discovered at boot, never built in: the app is one immutable zip promoted to
 * every environment, so the deployment writes the facts beside index.html as
 * /manifest.json and the SDK reads that first thing — an override, then the
 * manifest, then the local ports on localhost, and a loud failure anywhere else
 * (the client SDK's serviceDiscovery.js). Deriving `administration-server.<env>`
 * from this app's own hostname is retired: it was a crutch for the file nobody
 * wrote. The SDK then points every client — the administration service, the
 * identity endpoints on vibe-server, the event runtime — at what was found, with
 * the session token on each request and one shared 401 handler; nothing about
 * the credential is written here.
 *
 * Split out of `main.js` because it is the only part of boot with a decision in
 * it. Everything else there is `app.use(...)`.
 *
 * @param {Object} [options]
 * @param {Object} [options.overrides] Handed to discovery — tests only.
 * @param {Function} [options.fetch]   The fetch discovery reads the manifest with — tests only.
 * @param {Object} [options.location]  The page location discovery reads — tests only.
 * @param {Object} [options.logger]    Where the SDK logs; the console by default.
 * @param {Object} [options.storage]   Where the session token lives (a device-storage port); localStorage by default.
 * @returns {Promise<Object>} What the SDK answered — the base URLs it is now pointed at.
 */
export async function configureBackend({ overrides, fetch, location, logger, storage } = {}) {
    configureLogger({ logger: logger ?? console })
    // The SDK's device-storage port over localStorage — the desktop's answer to
    // the portal's Capacitor Preferences. The port forwards it to the identity
    // lib, which keeps the session token there; every call resolves, never
    // rejects, which matters on the boot path.
    configurePorts({ deviceStorage: storage ?? createDeviceStorage(createWebStorageDriver(), { name: 'localStorage' }) })
    const services = await discoverServices({ overrides, fetch, location })
    return configureBackends(services)
}
